import BaseApiService from './BaseApiService'

/**
 * A team's billing standing (FR-1.7.2 metering).
 *
 * Deliberately separate from the usage totals it sits beside, because the two are scoped
 * differently: the totals honour the selected workspace, while billing is a TEAM concern and counts
 * every workspace's renders.
 */
export interface UsageStanding {
  /** 'free' | 'standard' | 'enhanced' | 'ultimate'. */
  planCode: string
  /** An outstanding balance could not be collected; rendering is refused until it clears. */
  isSuspended: boolean
  hasPaymentMethod: boolean
  /** Whether this plan needs a card at all. Free does not. */
  requiresPaymentMethod: boolean
  /** Documents included this month. Set on Free only; the paid plans cap pages instead. */
  includedDocuments: number | null
  /** Whether usage past the included quantity is billed rather than refused. */
  allowsOverage: boolean
  usedThisPeriod: number
  /** Pages included in the monthly minimum, or null when the plan prices every page. */
  includedPages: number | null
  /** The plan's minimum monthly cost, in whole cents. */
  monthlyMinimumCents: number | null
  /** Cents per page beyond the allowance. Fractional — Enhanced's is 0.89. */
  perPageCents: number | null
  /** Pages rendered this period, from the same aggregate the invoice is built from. */
  pagesThisPeriod: number
}

export interface UsageSummary {
  totalRenders: number
  totalPages: number
  totalBytes: number
  periodStart: string
  periodEnd: string
  standing: UsageStanding
}

export interface RenderDayStats {
  day: string
  totalRenders: number
  successRenders: number
  failedRenders: number
  avgDurationMs: number | null
}

export interface LatencyPercentiles {
  p50DurationMs: number
  p95DurationMs: number
  p99DurationMs: number
}

export interface AnalyticsResponse {
  rows: RenderDayStats[]
  percentiles: LatencyPercentiles | null
}

class UsageApiService extends BaseApiService {
  getSummary(): Promise<UsageSummary> {
    return this.get('/v1/usage/summary')
  }

  getAnalytics(days = 30): Promise<AnalyticsResponse> {
    return this.get(`/v1/usage/analytics?days=${days}`)
  }
}

export const usageApiService = new UsageApiService()
